import { toast } from 'sonner';
import { telechargerMonBulletinPdf, useMesBulletins } from '@/api/hooks';
import { Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr, montant, nombre } from '@/lib/utils';

const usd = (n: number | null | undefined) => montant(n, true);

/** Mes bulletins de paie (lot HP-5) : paies clôturées de l'agent, montants et téléchargement du bulletin PDF. */
export function MesBulletins() {
  const { data, isLoading, error } = useMesBulletins();
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const dernier = data[0];
  return (
    <div className="space-y-6">
      <PageHeader titre="Mes bulletins de paie" sousTitre="Bulletins publiés à la clôture de chaque paie (montants en USD, impôt calculé en CDF au taux figé de la paie). Toute erreur se signale aux Ressources humaines ; une régularisation se fait sur la paie suivante." />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Bulletins publiés" valeur={data.length} detail={dernier ? `dernier : ${dernier.mois}` : 'aucune paie clôturée'} />
        <Kpi label="Dernier net à payer" valeur={dernier ? usd(dernier.netAPayer) : '—'} detail={dernier ? `payé le ${dateFr(dernier.payeLe)}` : ''} accent={dernier ? 'ok' : undefined} />
        <Kpi label="Dernières retenues légales" valeur={dernier ? usd(dernier.cnss + dernier.impotUsd) : '—'} detail={dernier ? `CNSS ${usd(dernier.cnss)} · impôt ${usd(dernier.impotUsd)}` : ''} />
        <Kpi label="Dernières retenues de prêts" valeur={dernier ? usd(dernier.retenuesPrets) : '—'} detail="échéances confirmées à la clôture" />
      </div>
      <Card data-testid="mes-bulletins">
        <CardHeader>
          <CardTitle>Bulletins</CardTitle>
          <CardDescription>Un bulletin par paie clôturée ; le PDF reprend chaque ligne de calcul.</CardDescription>
        </CardHeader>
        <CardContent>
          {!data.length ? (
            <Vide message="Aucun bulletin publié pour le moment." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mois</TableHead>
                  <TableHead className="hidden md:table-cell">Période</TableHead>
                  <TableHead className="hidden md:table-cell text-right">Jours payés</TableHead>
                  <TableHead className="text-right">Gains</TableHead>
                  <TableHead className="hidden lg:table-cell text-right">Retenues</TableHead>
                  <TableHead className="text-right">Net à payer</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((b) => (
                  <TableRow key={b.calculId}>
                    <TableCell className="font-medium">
                      {b.mois}
                      <div className="text-xs text-muted-foreground">publié le {dateFr(b.clotureLe)}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                      du {dateFr(b.debut)} au {dateFr(b.fin)}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-right">{nombre(b.joursPayes, 2)}</TableCell>
                    <TableCell className="text-right">{usd(b.totalGains)}</TableCell>
                    <TableCell className="hidden lg:table-cell text-right">{usd(b.totalRetenues)}</TableCell>
                    <TableCell className="text-right font-medium">{usd(b.netAPayer)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => telechargerMonBulletinPdf(b.calculId, b.mois).catch((e) => toast.error(e instanceof Error ? e.message : 'Téléchargement impossible.'))}>
                        Bulletin (PDF)
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
